import { useEffect, useRef, useState, useCallback } from "react";
import api, { API } from "@/lib/api";

// Sincronização colaborativa em tempo real por turma+oficina.
// WebSocket com reconexão (backoff) + fallback de polling (6s) quando o socket cair.
// Sempre que chegar um evento relevante, busca o estado (/collab/state) e dispara onEvent.
export function useCollabRoom(turmaId, oficinaId, onEvent) {
  const [state, setState] = useState({ locks: [], avaliacoes: [], progress: { avaliados: 0, total: 0 }, me: null });
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const backoffRef = useRef(1000);
  const closedRef = useRef(false);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const refresh = useCallback(async () => {
    if (!turmaId || !oficinaId) return null;
    try {
      const { data } = await api.get(`/collab/state?turma_id=${turmaId}&oficina_id=${oficinaId}`, { silent: true });
      setState(data);
      return data;
    } catch { return null; }
  }, [turmaId, oficinaId]);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(refresh, 6000);
  }, [refresh]);
  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  useEffect(() => {
    if (!turmaId || !oficinaId) return;
    closedRef.current = false;
    let heartbeat = null;

    const connect = () => {
      if (closedRef.current) return;
      const token = localStorage.getItem("metron_token");
      if (!token) { startPolling(); return; }
      const wsBase = API.replace(/^http/, "ws").replace(/\/api$/, "");
      const url = `${wsBase}/api/ws/collab?token=${encodeURIComponent(token)}&turma_id=${turmaId}&oficina_id=${oficinaId}`;
      let ws;
      try { ws = new WebSocket(url); } catch { startPolling(); return; }
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        backoffRef.current = 1000;
        stopPolling();
        refresh();
        heartbeat = setInterval(() => { try { ws.send("ping"); } catch { /* noop */ } }, 25000);
      };
      ws.onmessage = async (ev) => {
        let msg = {};
        try { msg = JSON.parse(ev.data); } catch { return; }
        const data = await refresh();
        if (onEventRef.current) onEventRef.current(msg, data);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
      ws.onclose = () => {
        setConnected(false);
        if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
        if (closedRef.current) return;
        // Fallback imediato por polling + tentativa de reconexão com backoff
        startPolling();
        const delay = Math.min(backoffRef.current, 15000);
        backoffRef.current = Math.min(backoffRef.current * 2, 15000);
        setTimeout(connect, delay);
      };
    };

    refresh();
    connect();

    return () => {
      closedRef.current = true;
      if (heartbeat) clearInterval(heartbeat);
      stopPolling();
      try { wsRef.current && wsRef.current.close(); } catch { /* noop */ }
    };
  }, [turmaId, oficinaId, refresh, startPolling, stopPolling]);

  return { state, connected, refresh };
}
